import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import { CONTACT_API_URL } from '../../config/api';
import { normalizeApiResponse } from '../../utils/api';
import { RECAPTCHA_SITE_KEY } from '../../config/recaptcha';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);

  useEffect(() => {
    // Handle hash navigation on page load
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    // Wait for reCAPTCHA script to load
    const loadRecaptcha = () => {
      if (window.grecaptcha && recaptchaRef.current) {
        try {
          const widgetId = window.grecaptcha.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            callback: (token) => {
              setRecaptchaToken(token);
            },
            'expired-callback': () => {
              setRecaptchaToken('');
            },
            'error-callback': () => {
              setRecaptchaToken('');
              console.error('reCAPTCHA error occurred');
            }
          });
          recaptchaRef.current.setAttribute('data-widget-id', widgetId);
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      } else if (!window.grecaptcha) {
        // Retry after a short delay if script hasn't loaded
        setTimeout(loadRecaptcha, 100);
      }
    };

    // Check if script is already loaded
    if (document.readyState === 'complete') {
      loadRecaptcha();
    } else {
      window.addEventListener('load', loadRecaptcha);
    }

    return () => {
      window.removeEventListener('load', loadRecaptcha);
    };
  }, []);

  const validateForm = () => {
    const { name, email, message } = formData;

    if (!name || !email || !message) {
      alert('Please fill in all required fields.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address.');
      return false;
    }

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      alert('Please complete the reCAPTCHA verification.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      formDataToSend.append('Name', formData.name);
      formDataToSend.append('Email', formData.email);
      if (formData.company) {
        formDataToSend.append('Company', formData.company);
      }
      formDataToSend.append('Message', formData.message);
      
      // Add reCAPTCHA token
      if (!recaptchaToken) {
        setSubmitMessage('Please complete the reCAPTCHA verification.');
        setIsLoading(false);
        return;
      }
      formDataToSend.append('RecaptchaToken', recaptchaToken);

      const response = await fetch(`${CONTACT_API_URL}/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      // Resilient parsing: handle non-JSON error bodies
      const contentType = response.headers.get('content-type') || '';
      let result;
      if (contentType.includes('application/json')) {
        try {
          const json = await response.json();
          result = normalizeApiResponse(json);
        } catch (e) {
          result = { success: response.ok, message: `Request ${response.ok ? 'succeeded' : 'failed'} (invalid JSON response)` };
        }
      } else {
        const text = await response.text();
        result = { success: response.ok, message: text || `HTTP ${response.status} ${response.statusText}` };
      }

      if (result.success) {
        setIsSubmitted(true);
        setSubmitMessage(result.message || 'Message sent successfully! We will get back to you soon.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
        setRecaptchaToken('');
        // Reset reCAPTCHA
        if (window.grecaptcha && recaptchaRef.current) {
          const widgetId = recaptchaRef.current.getAttribute('data-widget-id');
          if (widgetId) {
            window.grecaptcha.reset(widgetId);
          }
        }
      } else {
        setSubmitMessage(result.message || 'Submission failed. Please check your details and try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage('An error occurred while sending your message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <video className="hero-video" autoPlay muted loop>
          <source src={process.env.PUBLIC_URL + '/images/agentserver.webm'} type="video/webm" />
          Your browser does not support the video tag.
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Transforming Business Through Advanced AI & Technology</h1>
          <p>Empowering businesses with cutting-edge AI, GIS, and immersive technology solutions. We transform ideas into intelligent, scalable software that drives innovation and growth.</p>
          <div className="cta-buttons">
            <button onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })} className="btn btn-primary">
              Explore Services
            </button>
            <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="btn btn-secondary">
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive technology solutions designed to accelerate your business transformation</p>
          </div>
          <div className="services-grid">
            <div className="service-card fade-in-up">
              <div className="service-icon">
                <i className="fas fa-robot"></i>
              </div>
              <h3>AI-Based Solutions</h3>
              <p>Intelligent autonomous agents, RAG-based systems, and advanced AI applications that can reason, plan, and execute complex tasks. Our AI systems learn, adapt, and make decisions to optimize your business processes.</p>
            </div>
            <div className="service-card fade-in-up">
              <div className="service-icon">
                <i className="fas fa-code-branch"></i>
              </div>
              <h3>AI-Enabled SDLC</h3>
              <p>Revolutionary software development lifecycle enhanced with AI capabilities. From automated code generation and testing to intelligent project management and deployment, we streamline your development process.</p>
            </div>
            <div className="service-card fade-in-up">
              <div className="service-icon">
                <i className="fas fa-vr-cardboard"></i>
              </div>
              <h3>Immersive Technology Solutions</h3>
              <p>Cutting-edge AR/VR experiences, GIS solutions, and spatial computing applications that bridge the digital and physical worlds. From virtual training environments to augmented reality applications, we create engaging interactive solutions.</p>
            </div>
            <div className="service-card fade-in-up">
              <div className="service-icon">
                <i className="fas fa-map-marked-alt"></i>
              </div>
              <h3>GIS-Based Application Development</h3>
              <p>Advanced geospatial intelligence and mapping solutions. From location analytics to spatial data visualization, we help you make data-driven decisions with geographic context through custom GIS applications.</p>
            </div>
            <div className="service-card fade-in-up">
              <div className="service-icon">
                <i className="fas fa-sitemap"></i>
              </div>
              <h3>Enterprise Architecture Consulting</h3>
              <p>Strategic technology architecture that scales with your business. We design robust, scalable systems that support growth and innovation, ensuring your technology infrastructure aligns with your business objectives.</p>
            </div>
            <div className="service-card fade-in-up">
              <div className="service-icon">
                <i className="fas fa-palette"></i>
              </div>
              <h3>UI/UX Design Consulting</h3>
              <p>Full-stack product design covering micro-frontend architecture, atomic component systems with design tokens and accessibility, resilient state/data flows, performance and routing strategy, plus frontend DevOps and test automation for reliable enterprise rollouts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Stand Out Section */}
      <section id="differentiators" className="differentiators">
        <div className="container">
          <div className="section-header">
            <h2>How We Stand Out</h2>
            <p>Revolutionary AI-first approach that transforms traditional software development</p>
          </div>
          
          <div className="differentiators-grid">
            <div className="differentiator-card fade-in-up">
              <div className="differentiator-icon">
                <i className="fas fa-rocket"></i>
              </div>
              <h3>AI-Integrated SDLC</h3>
              <p>We integrate AI tools throughout the software development lifecycle to accelerate delivery and improve quality. Our approach reduces development time while maintaining high standards.</p>
              <ul className="feature-list">
                <li><i className="fas fa-check"></i> AI-powered code generation</li>
                <li><i className="fas fa-check"></i> Automated testing and QA</li>
                <li><i className="fas fa-check"></i> Smart deployment and monitoring</li>
                <li><i className="fas fa-check"></i> Performance optimization</li>
              </ul>
            </div>

            <div className="differentiator-card fade-in-up">
              <div className="differentiator-icon">
                <i className="fas fa-brain"></i>
              </div>
              <h3>AI-Enhanced Documentation & Architecture</h3>
              <p>We use AI to streamline the preparation of BRDs, PRDs, Information Architecture, and Application Architecture documents. This ensures comprehensive and accurate project planning from the start.</p>
              <ul className="feature-list">
                <li><i className="fas fa-check"></i> AI-powered BRD and PRD preparation</li>
                <li><i className="fas fa-check"></i> Automated Information Architecture design</li>
                <li><i className="fas fa-check"></i> Intelligent Application Architecture planning</li>
                <li><i className="fas fa-check"></i> Smart requirement analysis and validation</li>
              </ul>
            </div>

            <div className="differentiator-card fade-in-up">
              <div className="differentiator-icon">
                <i className="fas fa-robot"></i>
              </div>
              <h3>Agentic AI Systems Design</h3>
              <p>We design and implement intelligent AI systems that can reason, plan, and execute tasks autonomously. Our multi-agent architectures enable smart decision-making and adaptive behavior.</p>
              <ul className="feature-list">
                <li><i className="fas fa-check"></i> Multi-agent system architecture design</li>
                <li><i className="fas fa-check"></i> Autonomous reasoning and planning capabilities</li>
                <li><i className="fas fa-check"></i> Continuous learning and adaptation</li>
                <li><i className="fas fa-check"></i> Human-AI collaboration frameworks</li>
              </ul>
            </div>

            <div className="differentiator-card fade-in-up">
              <div className="differentiator-icon">
                <i className="fas fa-tools"></i>
              </div>
              <h3>AI-Powered Development Tools</h3>
              <p>Our AI development toolkit accelerates software creation through intelligent code completion, automated testing, and performance optimization. We use cutting-edge AI tools to deliver better results faster.</p>
              <ul className="feature-list">
                <li><i className="fas fa-check"></i> Intelligent code completion and suggestions</li>
                <li><i className="fas fa-check"></i> AI-driven debugging and error resolution</li>
                <li><i className="fas fa-check"></i> Automated performance optimization</li>
                <li><i className="fas fa-check"></i> Smart documentation generation</li>
              </ul>
            </div>
          </div>

          <div className="process-overview fade-in-up">
            <h3>Our AI-First Development Process</h3>
            <div className="process-steps">
              <div className="process-step">
                <div className="step-number">1</div>
                <h4>AI-Enhanced Discovery</h4>
                <p>Intelligent requirement gathering and documentation preparation using AI tools</p>
              </div>
              <div className="process-step">
                <div className="step-number">2</div>
                <h4>AI-Powered Design</h4>
                <p>Automated architecture generation and intelligent system design</p>
              </div>
              <div className="process-step">
                <div className="step-number">3</div>
                <h4>Intelligent Development</h4>
                <p>AI-assisted coding, automated testing, and continuous integration</p>
              </div>
              <div className="process-step">
                <div className="step-number">4</div>
                <h4>Smart Deployment</h4>
                <p>AI-driven deployment strategies with intelligent monitoring</p>
              </div>
              <div className="process-step">
                <div className="step-number">5</div>
                <h4>Agentic AI</h4>
                <p>Implementation of autonomous AI agents for intelligent decision-making and task execution</p>
              </div>
              <div className="process-step">
                <div className="step-number">6</div>
                <h4>Open Source AI Development</h4>
                <p>Leveraging and contributing to open source AI frameworks and tools for innovation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunities Section */}
      <section id="opportunities" className="opportunities">
        <div className="container">
          <div className="section-header">
            <h2>Join Our Team</h2>
            <p>Shape the future of AI technology with us</p>
          </div>
          <div className="opportunity-card">
            <h3>Engineering Internship Program</h3>
            <p>We're inviting passionate engineering students to join our team and work on cutting-edge agentic AI projects. This is your opportunity to gain hands-on experience with the latest AI technologies while contributing to real-world solutions.</p>
            
            <div className="opportunity-features">
              <div className="feature">
                <i className="fas fa-code"></i>
                <h4>Hands-on Development</h4>
                <p>Work on real AI projects with mentorship from experienced engineers</p>
              </div>
              <div className="feature">
                <i className="fas fa-graduation-cap"></i>
                <h4>Learning & Growth</h4>
                <p>Access to cutting-edge AI tools and continuous learning opportunities</p>
              </div>
              <div className="feature">
                <i className="fas fa-users"></i>
                <h4>Team Collaboration</h4>
                <p>Collaborate with talented engineers and contribute to innovative solutions</p>
              </div>
              <div className="feature">
                <i className="fas fa-rocket"></i>
                <h4>Career Launch</h4>
                <p>Potential for full-time opportunities and career advancement</p>
              </div>
              <div className="feature">
                <i className="fas fa-lightbulb"></i>
                <h4>Innovation Focus</h4>
                <p>Work on cutting-edge AI research and experimental technologies</p>
              </div>
              <div className="feature">
                <i className="fas fa-globe"></i>
                <h4>Global Impact</h4>
                <p>Contribute to AI solutions that solve real-world problems globally</p>
              </div>
            </div>
            
            <Link to="/internship" className="btn btn-primary">Apply Now</Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <h2>Get In Touch</h2>
            <p>Ready to transform your business with intelligent solutions? Let's start a conversation.</p>
          </div>
          <div className="contact-form">
            {isSubmitted && (
              <div className="success-message" style={{ 
                background: '#48bb78', 
                color: 'white', 
                padding: '1rem', 
                borderRadius: '10px', 
                textAlign: 'center', 
                marginBottom: '1rem' 
              }}>
                <i className="fas fa-check-circle"></i> {submitMessage}
              </div>
            )}

            {submitMessage && !isSubmitted && (
              <div className="error-message" style={{ 
                background: '#e53e3e', 
                color: 'white', 
                padding: '1rem', 
                borderRadius: '10px', 
                textAlign: 'center', 
                marginBottom: '1rem' 
              }}>
                <i className="fas fa-exclamation-circle"></i> {submitMessage}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input 
                  type="text" 
                  id="company" 
                  name="company" 
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  placeholder="Tell us about your project or how we can help..." 
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Please verify you're not a robot *</label>
                <div 
                  ref={recaptchaRef}
                  style={{ marginTop: '10px' }}
                ></div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary" 
                style={{ width: '100%' }}
                disabled={isSubmitted || isLoading}
              >
                {isLoading ? 'Sending...' : isSubmitted ? 'Message Sent' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <p>&copy; 2025 SibylCues. All rights reserved. | Intelligent Software Solutions</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
